import { gql, useQuery } from '@apollo/client';
import {
  Accordion,
  AccordionHeader,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Group,
  Heading,
  Link,
} from '@apollo/orbit';
import React from 'react';
import { useLocation } from 'react-router-dom';

import {
  ChecksTableWorkflowCell,
  ChecksTableWorkflowCellWorkflowFragment,
} from 'src/app/graph/checksPage/checkWorkflowsListPage/checkWorkflowItem/ChecksTableWorkflowCell';
import { CheckWorkflowItemFragment } from 'src/app/graph/checksPage/checkWorkflowsListPage/checkWorkflowItem/CheckWorkflowItem';
import {
  CheckWorkflowStatusBadge,
  CheckWorkflowStatusBadgeCheckWorkflowFragment,
} from 'src/app/graph/checksPage/checkWorkflowsListPage/checkWorkflowItem/CheckWorkflowStatusBadge';
import {
  CheckWorkflowTaskItem,
  CheckWorkflowTaskItemFragment,
} from 'src/app/graph/checksPage/checkWorkflowsListPage/checkWorkflowItem/checkWorkflowTaskItem/CheckWorkflowTaskItem';
import { shouldDisplayTask } from 'src/app/graph/checksPage/checkWorkflowsListPage/checkWorkflowItem/getTaskDisplayStatus';
import {
  SuccessfulCheckWorkflowItem,
  SuccessfulCheckWorkflowItemFragment,
} from 'src/app/graph/checksPage/checkWorkflowsListPage/checkWorkflowItem/SuccessfulCheckWorkflowItem';
import {
  TaskStatusIcon,
  TaskStatusIconTaskFragment,
} from 'src/app/graph/checksPage/taskStatusIcon/TaskStatusIcon';
import {
  proposalChecksListRouteConfig,
  proposalHomepageRouteConfig,
} from 'src/app/graph/routes';
import { ShimmerLoading } from 'src/components/common/loading/ShimmerLoading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly';

export const ChecksOverviewSection = ({
  latestRevisionId,
}: {
  latestRevisionId: string;
}) => {
  const { graphId, graphVariant } = useRouteParams(proposalHomepageRouteConfig);

  const proposalsUseHasWarnings = useLDFlag(
    'tooling-proposals-use-has-warnings',
  );
  const { data: checksOverviewData, loading: checksOverviewLoading } = useQuery<
    GraphQLTypes.CheckOverviewSectionQuery,
    GraphQLTypes.CheckOverviewSectionQueryVariables
  >(
    gql`
      query CheckOverviewSectionQuery(
        $graphId: ID!
        $checkWorkflowsFilter: CheckFilterInput
        $proposalsUseHasWarnings: Boolean!
      ) {
        service(id: $graphId) {
          id
          totalCheckWorkflowCount(filter: $checkWorkflowsFilter)
          checkWorkflows(filter: $checkWorkflowsFilter, limit: 5) {
            id
            ...CheckWorkflowItemFragment
            ...CheckWorkflowStatusBadgeCheckWorkflowFragment
            ...ChecksTableWorkflowCellWorkflowFragment
            ...SuccessfulCheckWorkflowItemFragment
            tasks {
              id
              ...CheckWorkflowTaskItemFragment
              ...TaskStatusIconTaskFragment
            }
          }
        }
      }
      ${CheckWorkflowItemFragment}
      ${SuccessfulCheckWorkflowItemFragment}
      ${CheckWorkflowTaskItemFragment}
      ${CheckWorkflowStatusBadgeCheckWorkflowFragment}
      ${ChecksTableWorkflowCellWorkflowFragment}
      ${TaskStatusIconTaskFragment}
    `,
    {
      variables: {
        graphId,
        checkWorkflowsFilter: {
          includeProposalChecks: true,
          variants: [graphVariant],
        },
        proposalsUseHasWarnings,
      },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const location = useLocation();

  const totalCheckWorkflowCount =
    checksOverviewData?.service?.totalCheckWorkflowCount ?? 0;
  const checkWorkflows = checksOverviewData?.service?.checkWorkflows ?? [];

  return (
    <div>
      <div className="flex justify-between border-b border-primary py-3">
        <Heading size="lg">Latest checks</Heading>
        <div>
          <Link
            className="text-sm"
            to={proposalChecksListRouteConfig.locationFrom({
              fromRouteConfig: proposalHomepageRouteConfig,
              location,
            })}
          >
            View all checks
          </Link>
        </div>
      </div>
      <Tooltip label={latestRevisionId}>
        <Heading as="h3" className="pb-2 pt-3">
          Revision {latestRevisionId.substring(0, 8)}
        </Heading>
      </Tooltip>
      {checksOverviewLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <ShimmerLoading
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="h-[4.5rem] border border-primary py-3"
            />
          ))}
        </div>
      ) : (
        <>
          {checkWorkflows.map((workflow) => {
            return (
              <ChecksOverviewSectionWorkflowItem
                key={workflow.id}
                workflow={workflow}
              />
            );
          })}
          {totalCheckWorkflowCount > 5 && (
            <Link
              className="text-sm"
              to={proposalChecksListRouteConfig.locationFrom({
                fromRouteConfig: proposalHomepageRouteConfig,
                location,
                patch: {
                  shouldHighlightLatest: 'true',
                },
              })}
            >
              Show all workflows
            </Link>
          )}
        </>
      )}
    </div>
  );
};

const ChecksOverviewSectionWorkflowItem = ({
  workflow,
}: {
  workflow: NonNullable<
    GraphQLTypes.CheckOverviewSectionQuery['service']
  >['checkWorkflows'][number];
}) => {
  const shouldDescriptionsWarn = useLDFlag(
    'gov-proposal-checks-descriptions-warn',
  );
  const proposalsUseHasWarnings = useLDFlag(
    'tooling-proposals-use-has-warnings',
  );
  const thresholdCrossed: number | null | undefined = (
    workflow.tasks.find(
      (task) => task.__typename === 'OperationsCheckTask',
    ) as Extract<
      GraphQLTypes.CheckWorkflowTaskItemFragment,
      { __typename: 'OperationsCheckTask' }
    >
  )?.result?.crossedOperationThreshold;
  const crossedThreshold: boolean =
    thresholdCrossed !== null && thresholdCrossed !== undefined;

  const hasOperationTask =
    workflow.tasks.findIndex(
      (task) => task.__typename === 'OperationsCheckTask',
    ) > -1;

  const displayedTasks = workflow.tasks.filter((task) =>
    shouldDisplayTask(task, shouldDescriptionsWarn, proposalsUseHasWarnings),
  );

  return (
    <Accordion allowToggle className="mb-2 cursor-pointer">
      <AccordionItem>
        <AccordionHeader>
          <Group justify="between">
            <div className="w-0 flex-1">
              <div className="flex flex-col truncate text-base text-secondary">
                <ChecksTableWorkflowCell workflow={workflow} />
              </div>
            </div>
            <div className="flex items-center">
              <CheckWorkflowStatusBadge workflow={workflow} />
            </div>
          </Group>
          <AccordionIcon />
        </AccordionHeader>
        <AccordionPanel className="flex flex-col gap-4">
          <Heading as="h4" variant="title">
            TASKS
          </Heading>
          <Tooltip
            disabled={!crossedThreshold}
            label={
              <div>
                We have detected more than {format.requests(thresholdCrossed)}{' '}
                unique operations. Any additional operations beyond these have
                not been checked and may be affected.
              </div>
            }
          >
            <div className="w-full">
              {displayedTasks.length === 0 ? (
                <SuccessfulCheckWorkflowItem workflow={workflow} />
              ) : (
                displayedTasks.map((task) => (
                  <CheckWorkflowTaskItem
                    task={task}
                    key={task.id}
                    crossedThreshold={crossedThreshold}
                  />
                ))
              )}

              {!hasOperationTask && (
                <div className="ml-2 mt-1 flex items-center">
                  <TaskStatusIcon
                    className="size-4"
                    task={GraphQLTypes.CheckWorkflowTaskStatus.BLOCKED}
                  />
                  <p className="ml-2 text-base text-secondary">
                    Operations checks disabled
                  </p>
                </div>
              )}
            </div>
          </Tooltip>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
